import { ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table as QTable,
  TableHeaderRow,
  TableHeaderCell,
  TableRow,
  TableCell
} from 'qampo-ui-library';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import { SORT_LOCALE } from '../../constants/sortConstants';
import { createValueComparator } from '../../utils/sortUtils';
import type { SortableValue } from '../../utils/sortUtils';
import styles from './Table.module.css';

// A floor, not a fixed height: the library's `.tableRow` pins `height: 70px`,
// so rows pair this with `height: auto` to let a wrapping value grow the row
// instead of painting over the row below it (REH-355).
const MIN_ROW_HEIGHT = 48;

export type SortDirection = 'asc' | 'desc';

export interface TableSortState {
  key: string;
  direction: SortDirection;
}

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T, index: number) => ReactNode;
  width?: string | number;
  headerAlign?: 'left' | 'center' | 'right';
  cellStyle?: React.CSSProperties;
  // When true, the header becomes a sort toggle. `sortValue` provides the
  // comparable value for ordering (required for sorting to take effect).
  sortable?: boolean;
  sortValue?: (row: T) => SortableValue;
}

interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  getRowKey?: (row: T, index: number) => string | number;
  style?: React.CSSProperties;
  footer?: ReactNode;
  getRowStyles?: (row: T, index: number) => React.CSSProperties;
  minWidth?: number | string;
  // Controlled sort state. When provided alongside sortable columns, the table
  // orders `data` by the active column's `sortValue` and renders sort toggles.
  sort?: TableSortState | null;
  onSortChange?: (key: string) => void;
}

function getCellStyles(col: TableColumn<unknown>, isHeader?: boolean) {
  const style: React.CSSProperties = {};
  if (col.width) {
    style.flexGrow = 0;
    style.flexShrink = 0;
  }

  style.color = 'var(--color-text-primary)';
  style.padding = 0;
  if (isHeader) {
    style.fontWeight = 700;

    if (col.headerAlign) {
      style.justifyContent = col.headerAlign;
    }
  } else {
    style.fontWeight = 400;
  }

  if (col.cellStyle) Object.assign(style, col.cellStyle);

  return Object.keys(style).length ? { root: style } : undefined;
}

export function Table<T>({
  columns,
  data,
  getRowKey,
  style,
  footer,
  getRowStyles,
  minWidth,
  sort,
  onSortChange
}: Readonly<TableProps<T>>) {
  const { t } = useTranslation();

  const tableStyle: React.CSSProperties = {
    ...(minWidth !== undefined
      ? { minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth }
      : {}),
    ...style
  };

  const activeSortColumn = useMemo(
    () => (sort ? columns.find((col) => col.key === sort.key) : undefined),
    [columns, sort]
  );

  // A sort key naming no sortable column renders the rows unsorted while the
  // header keeps showing an active chevron, so the table asserts an order it
  // does not have. Nothing surfaces that on its own: both keys are plain
  // strings, so a typo or a renamed column still compiles. Two tables now open
  // on a default sort and neither knows about the other, so the check lives
  // here rather than in each consumer.
  //
  // In an effect rather than in the sort memo below, so it reports once per
  // offending key instead of on every recompute. That matters because at least
  // one consumer's selector rebuilds its array on every Redux dispatch, which
  // would otherwise reprint this for as long as the page stayed open.
  useEffect(() => {
    if (!sort || activeSortColumn?.sortValue) return;
    console.error(
      `Table: sort key "${sort.key}" matches no sortable column (columns: ${columns
        .map((col) => col.key)
        .join(', ')}). Rendering unsorted.`
    );
  }, [sort, activeSortColumn, columns]);

  const sortedData = useMemo(() => {
    if (!sort || !activeSortColumn?.sortValue) return data;
    const getValue = activeSortColumn.sortValue;
    // Pinned to Danish rather than read off the interface language: see
    // SORT_LOCALE for why the device and the UI are both the wrong axis.
    const compareValues = createValueComparator(SORT_LOCALE);
    const directionFactor = sort.direction === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => compareValues(getValue(a), getValue(b)) * directionFactor);
  }, [data, sort, activeSortColumn]);

  const getSortIndicatorClass = (col: TableColumn<T>): string => {
    if (sort?.key !== col.key) return styles.sortInactive;
    return sort.direction === 'asc' ? styles.sortAsc : styles.sortDesc;
  };

  // The chevron is the only sign of sort state, and it carries none of it to a
  // screen reader. aria-sort on the th would be the right home for this, but the
  // library's TableHeaderCell accepts no arbitrary props, so the state rides in
  // the button's accessible name: "Name sorted ascending". Ascending/descending
  // rather than A-Z, because the same header serves numeric columns too.
  const getSortStateLabel = (col: TableColumn<T>): string => {
    if (sort?.key !== col.key) return t('table-sort-none');
    return sort.direction === 'asc' ? t('table-sort-ascending') : t('table-sort-descending');
  };

  const renderHeader = (col: TableColumn<T>): ReactNode => {
    if (!col.sortable) return col.header;
    return (
      <button type="button" className={styles.sortButton} onClick={() => onSortChange?.(col.key)}>
        {col.header}
        <span className={styles.srOnly}>{getSortStateLabel(col)}</span>
        <ChevronIcon
          className={`${styles.sortIcon} ${getSortIndicatorClass(col)}`}
          aria-hidden="true"
        />
      </button>
    );
  };

  return (
    <div className={styles.scrollWrapper}>
      {/* Activating a sort header changes only that button's accessible name,
          and screen readers are inconsistent about re-announcing the element
          already focused. This region is what actually reports the change. It
          renders even with no sort so the region exists before it updates,
          which is what makes the first change announce. */}
      <span role="status" aria-live="polite" className={styles.srOnly}>
        {sort && activeSortColumn ? (
          <>
            {activeSortColumn.header} {getSortStateLabel(activeSortColumn)}
          </>
        ) : (
          ''
        )}
      </span>
      <QTable classes={styles.table} styles={{ root: tableStyle }}>
        <thead>
          <TableHeaderRow
            classes={styles.headerRow}
            styles={{
              root: {
                height: 'auto',
                minHeight: MIN_ROW_HEIGHT
              }
            }}
          >
            {columns.map((col) => (
              <TableHeaderCell
                key={col.key}
                width={typeof col.width === 'number' ? `${col.width}` : col.width}
                classes={styles.headerCell}
                styles={getCellStyles(col as TableColumn<unknown>, true)}
              >
                {renderHeader(col)}
              </TableHeaderCell>
            ))}
          </TableHeaderRow>
        </thead>
        <tbody style={{ gap: 0 }}>
          {sortedData.map((row, index) => (
            <TableRow
              key={getRowKey ? getRowKey(row, index) : index}
              classes={styles.row}
              styles={{
                root: {
                  height: 'auto',
                  minHeight: MIN_ROW_HEIGHT,
                  borderRadius: 0,
                  borderBottom: '1px solid var(--color-border-secondary)',
                  ...(getRowStyles ? getRowStyles(row, index) : {})
                }
              }}
            >
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  width={typeof col.width === 'number' ? `${col.width}` : col.width}
                  classes={styles.cell}
                  styles={getCellStyles(col as TableColumn<unknown>)}
                >
                  {col.render(row, index)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
        {footer && (
          <tfoot style={{ height: MIN_ROW_HEIGHT }}>
            <tr style={{ padding: 'var(--spacing-sm) var(--spacing-lg)' }}>
              <td colSpan={columns.length} className={styles.footerCell}>
                {footer}
              </td>
            </tr>
          </tfoot>
        )}
      </QTable>
    </div>
  );
}
